import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    documentId: Number
  }

  static targets = ["form", "items"]

  connect() {
    this.abortController = null
    this.refreshTimer = null
    this.onFormChange = this.onFormChange.bind(this)
    this.onFormInput = this.onFormInput.bind(this)

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("change", this.onFormChange)
      this.formTarget.addEventListener("input", this.onFormInput)
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("change", this.onFormChange)
      this.formTarget.removeEventListener("input", this.onFormInput)
    }

    if (this.abortController) this.abortController.abort()
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
  }

  onFormChange(event) {
    if (!this.isAnswerField(event.target)) return
    this.scheduleRefresh({ debounced: false })
  }

  onFormInput(event) {
    if (!this.isAnswerField(event.target)) return
    this.scheduleRefresh({ debounced: true })
  }

  isAnswerField(target) {
    if (!target) return false
    const fieldName = target.getAttribute("name") || ""
    return fieldName.includes("[answer]")
  }

  scheduleRefresh({ debounced }) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    if (!debounced) {
      this.refreshRuntime()
      return
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null
      this.refreshRuntime()
    }, 250)
  }

  serializeDraftRows() {
    if (!this.hasFormTarget) return []

    const rows = []
    this.formTarget.querySelectorAll(".js-runtime-item").forEach((item) => {
      const sourceTypeInput = item.querySelector('input[name*="[source_type]"]')
      const sourceIdInput = item.querySelector('input[name*="[source_id]"]')
      if (!sourceTypeInput || !sourceIdInput) return

      const checkboxInputs = item.querySelectorAll('input[type="checkbox"][name*="[answer]"]')
      let answerValue = null

      if (checkboxInputs.length > 0) {
        answerValue = Array.from(checkboxInputs)
          .filter((input) => input.checked)
          .map((input) => input.value)
      } else {
        const answerInput = item.querySelector('select[name*="[answer]"], textarea[name*="[answer]"], input[name*="[answer]"]')
        answerValue = answerInput ? answerInput.value : null
      }

      const explanationInput = item.querySelector('input[type="hidden"][name*="[explanation]"]')

      rows.push({
        source_type: sourceTypeInput.value,
        source_id: sourceIdInput.value,
        answer: answerValue,
        explanation: explanationInput ? explanationInput.value : ""
      })
    })

    return rows
  }

  async refreshRuntime() {
    if (!this.urlValue || !this.hasItemsTarget) return

    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin",
        signal: this.abortController.signal,
        body: JSON.stringify({
          document_id: this.documentIdValue,
          draft_answers_rows: this.serializeDraftRows()
        })
      })

      if (!response.ok) return

      const payload = await response.json()
      if (typeof payload.items_html !== "string") return

      this.itemsTarget.innerHTML = payload.items_html
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Template runtime refresh failed", error)
      }
    }
  }
}
